import axios, { GenericAbortSignal } from 'axios'

import BaseService from './BaseService'
import { NotificationStatusType } from '../util/notification/notification'
import defaults from '@/config'

class ReportingService extends BaseService {
  constructor(baseUrl: string) {
    super(baseUrl)
  }
}

class EmailReportingService extends ReportingService {
  private emailReportingOldUrl = defaults.emailReportingUrl

  getEmailStatsOld = async (query: { [key: string]: any }) => {
    let records = []
    let total = 0

    const stats = await axios.post(`${this.emailReportingOldUrl}/agent_reporting/email_stats_report/bulk_request`, {
      location_id: query.locationId,
      option: query.status === 'permanentFail' ? 'permanent_fail' : query.status,
      showCount: query.includeTotalCount,
      maxRows: query.limit,
      start: query.skip,
      bulk_req_id: query.sourceId,
    })
    records = stats.data.response.records
    total = query.includeTotalCount ? stats.data.response.total : 0

    return { records, total }
  }

  getEmailStatsNew = async (query: { [key: string]: any }) => {
    let records = []
    let total = 0

    const stats = await this.requests.get(`${query ? `?${new URLSearchParams(query).toString()}` : ''}`)
    records = stats.results.map(r => ({...r, email: r.email?.[0]}))
    total = query.includeTotalCount ? stats.total : 0

    return { records, total }
  }

  getEmailExportStats = async (query: { [key: string]: any }) => {
    let results = []
    let total = 0

    const stats = await this.requests.get(`${query ? `/export?${new URLSearchParams(query).toString()}` : ''}`)
    results = stats.results
    total = stats.total

    return { results, total }
  }

  getAggregateStatusData = async (query: { [key: string]: any }, useNewDataSource = true) => {
    if (useNewDataSource) {
      const response = await this.requests.get(
        `/aggregate${query ? `?${new URLSearchParams(query).toString()}` : ''}`
      )
      let results = response.results
      results = Object.keys(results).reduce((acc, curr) => ({...acc, [curr]: results[curr].value}), {})
      results['count'] = response.total
      return [results]
    }

    const response = await axios.post(`${this.emailReportingOldUrl}/agent_reporting/email_stats/bulk_request/`, {
      location_id: query.locationId,
      bulk_req_id: query.sourceId,
    })
    return response.data.response
  }

  getAggregateUsersStats = (body: { [key: string]: any }) => this.requests.post(`/aggregate/get-users-stats`, body)
}

export const emailReportingService = new EmailReportingService(BaseService.servicesConfig.EmailReporting as string)

class CallsReportingService extends ReportingService {
  getCallMetrics = body => this.requests.post('/get-call-metrics', body)
  getAllPhoneCallsNew = body => this.requests.post('/get-all-phone-calls-new', body)
}

export const callReportingService = new CallsReportingService(BaseService.servicesConfig.CallsReporting as string)

class SMSReportingService extends ReportingService {
  private emailReportingOldUrl = defaults.emailReportingUrl

  getAggregateUsersStats = (body: { [key: string]: any }) => this.requests.post(`/aggregate/get-users-stats`, body)

  getSmsExportStats = async (query: { [key: string]: any }) => {
    let results = []
    let total = 0

    const stats = await this.requests.get(`${query ? `/export?${new URLSearchParams(query).toString()}` : ''}`)
    results = stats.results
    total = stats.total

    return { results, total }
  }

  getAggregateStatusData = async (query: { [key: string]: any }, useNewDataSource = true) => {
    if (useNewDataSource) {
      const response = await this.requests.get(
        `/aggregate${query ? `?${new URLSearchParams(query).toString()}` : ''}`
      )
      let results = response.results
      results = Object.keys(results).reduce((acc, curr) => ({...acc, [curr]: results[curr].value}), {})
      results['count'] = response.total
      return [results]
    }

    const response = await axios.post(`${this.emailReportingOldUrl}/agent_reporting/sms_stats/bulk_request/`, {
      location_id: query.locationId,
      bulk_req_id: query.sourceId,
      user_id: query.userId,
    })
    return response.data.response
  }

  getStatsErrorCodes = async (query: { [key: string]: any }, useNewDataSource = true) => {
    if (useNewDataSource) {
      const response = await this.requests.get(
        `/stats-error-codes${query ? `?${new URLSearchParams(query).toString()}` : ''}`
      )
      return response
    }

    const response = await axios.post(`${this.emailReportingOldUrl}/agent_reporting/sms_stats_errorcodes`, {
      location_id: query.locationId,
      bulk_req_id: query.sourceId,
    })
    return response.data.response
  }

  getSMSOld = async (query: { [key: string]: any }) => {
    let records = []
    let total = 0

    let path = `${this.emailReportingOldUrl}/agent_reporting/sms_stats_report`
    let payload: any = {
      location_id: query.locationId,
      pagination: query.pagination,
    }

    if (query.source === 'bulk_request') {
      path += '/bulk_request'
      payload.bulk_req_id = query.sourceId
    } else {
      payload.campaign_id = query.sourceId
      if (query.subSourceId) {
        payload.campaign_step_id = query.subSourceId
      }
    }

    if (query.status === 'delivered') payload['showDelivered'] = true
    else if (query.status === 'clicked') payload['showClicked'] = true
    else if (query.status === 'failed') payload['showFailed'] = true

    const stats = await axios.post(path, payload)

    records = stats.data.page.pageRecords
    total = stats.data.queryTotalCount

    return { records, total }
  }

  getSMSNew = async (query: { [key: string]: string }) => {
    let records = []
    let total = 0

    delete query.pagination

    const data = await this.requests.get(`${query ? `?${new URLSearchParams(query).toString()}` : ''}`)

    records = data.results
    total = data.totalCount

    return { records, total }
  }
}

export const smsReportingService = new SMSReportingService(BaseService.servicesConfig.SMSReporting as string)

/**
 * Represents a service for reporting opportunities.
 * Extends the base ReportingService class.
 */
class OpportunitiesReportingService extends ReportingService {
  /** The URL for opportunities reporting service. */
  private opportunitiesUrl = defaults.opportunityReportingService

  /**
   * Retrieves opportunities statistics.
   * @param {object} body - The request body.
   * @returns {Promise<any>} A promise that resolves to the response from the server.
   */
  async getOpportunitiesStats(body: { [key: string]: any }): Promise<any> {
    return this.requests.post(`/opportunity-stats`, body)
  }

  /**
   * Retrieves leaderboard statistics.
   * @param {object} body - The request body.
   * @returns {Promise<any>} A promise that resolves to the response from the server.
   */
  async getLeaderBoardStats(body: { [key: string]: any }): Promise<any> {
    return this.requests.post(`/leaderboard-stats`, body)
  }

  /**
   * Retrieves conversion statistics.
   * @param {object} body - The request body.
   * @returns {Promise<any>} A promise that resolves to the response from the server.
   */
  async getConversionStats(body: { [key: string]: any }): Promise<any> {
    return this.requests.post(`/conversion-stats`, body)
  }

  /**
   * Retrieves sales velocity statistics.
   * @param {object} body - The request body.
   * @returns {Promise<any>} A promise that resolves to the response from the server.
   */
  async getSalesVelocityStats(body: { [key: string]: any }): Promise<any> {
    return this.requests.post(`/sales-velocity`, body)
  }
}

// Export an instance of OpportunitiesReportingService
export const opportunitiesReportingService = new OpportunitiesReportingService(
  BaseService.servicesConfig.OpportunitiesReporting as string
)

class ReportingNotificationService extends ReportingService {
  getAllNotifications = (query: {
    notificationStatus?: NotificationStatusType
    locationId?: string
    companyId: string
  }, signal?: GenericAbortSignal) => {
    const options = signal ? { signal } : {}

    return this.requests.get(`?${new URLSearchParams(getFilteredQuery(query)).toString() || ''}`, options)
  }

  getNotificationByConfig = (config: { [key: string]: any }, signal?: GenericAbortSignal) => {
    const options = signal ? { signal } : {}

    const url = `${config.endpoint}?${new URLSearchParams(getFilteredQuery(config.query)).toString() || ''}`
    config.body = config.body || {}
    switch (config.method) {
      case 'POST':
        return this.requests.post(url, config.body, options)
      case 'GET':
      default:
        return this.requests.get(url, options)
    }
  }

  markNotificationAsViewed = (notificationId: string, body: { locationId?: string; companyId: string }) => {
    return this.requests.put(`/${notificationId}/user/viewed?`, body)
  }
}

export const reportingNotificationService = new ReportingNotificationService(
  BaseService.servicesConfig.ReportingNotification as string
)

class GAReportingService extends ReportingService {
  getAccounts = (locationId: string) => this.requests.get(`/accounts?locationId=${locationId}`)
  getAccountProperties = (accountId: string, locationId: string) => this.requests.get(`/accounts/${accountId}/properties?locationId=${locationId}`)
}

export const gaReportingService = new GAReportingService(
  BaseService.servicesConfig.GAReporting as string
)


const getFilteredQuery = (query: { [key: string]: any }) => {
  return Object.fromEntries(
    Object.entries(query).filter(([_, v]) => !!v)
  )
}
